import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import TeamCrest from "./ui/TeamCrest";

const ALL = "";

/**
 * Keeps the selected team in the URL so it survives reloads and tab switches
 * (the league hubs only swap the `tab` param when you change tabs).
 */
export const useTeamFilterParam = (paramKey = "team") => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTeamId = searchParams.get(paramKey) || ALL;

  const setSelectedTeamId = useCallback(
    (next) => {
      const params = new URLSearchParams(searchParams);
      if (next) params.set(paramKey, String(next));
      else params.delete(paramKey);
      setSearchParams(params, { replace: true });
    },
    [paramKey, searchParams, setSearchParams]
  );

  return [selectedTeamId, setSelectedTeamId];
};

const TeamLogo = ({ team, size = 20 }) => (team?.logo_url ? <TeamCrest team={team} size={size} /> : null);

/**
 * Pill-shaped dropdown for narrowing fixtures / stats down to a single team.
 * `value` is "" for every team, otherwise the team id (compared as a string).
 */
const TeamFilter = ({
  teams = [],
  value = ALL,
  onChange,
  allLabel = "All teams",
  disabled = false,
  sx,
}) => {
  const current = String(value ?? ALL);
  const isFiltered = current !== ALL;
  const selectedTeam = teams.find((t) => String(t.id) === current);

  return (
    <Select
      value={teams.some((t) => String(t.id) === current) ? current : ALL}
      onChange={(event) => onChange(event.target.value)}
      displayEmpty
      disabled={disabled || teams.length === 0}
      size="small"
      aria-label="Filter by team"
      renderValue={(selected) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {selected === ALL ? (
            <GroupsIcon sx={{ fontSize: 18 }} />
          ) : (
            <TeamLogo team={selectedTeam} size={18} />
          )}
          <Typography
            component="span"
            noWrap
            sx={{ fontWeight: 700, fontSize: "0.8rem", lineHeight: 1.4 }}
          >
            {selected === ALL ? allLabel : selectedTeam?.name || "Team"}
          </Typography>
        </Box>
      )}
      MenuProps={{
        PaperProps: {
          sx: {
            borderRadius: 2,
            maxHeight: "min(70vh, 420px)",
            "& .MuiMenuItem-root": { fontSize: "0.85rem", fontWeight: 600, gap: 1 },
          },
        },
      }}
      sx={{
        maxWidth: 260,
        borderRadius: "999px",
        backgroundColor: isFiltered ? "#1a1a1a" : "#fff",
        color: isFiltered ? "#fff" : "#1a1a1a",
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          py: 0.85,
          pl: 1.75,
        },
        "& .MuiSelect-icon": { color: isFiltered ? "#fff" : "#666" },
        "& .MuiOutlinedInput-notchedOutline": {
          borderRadius: "999px",
          borderColor: isFiltered ? "#1a1a1a" : "#e0e0e0",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: isFiltered ? "#1a1a1a" : "#bdbdbd",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#1a1a1a",
          borderWidth: 1,
        },
        ...sx,
      }}
    >
      <MenuItem value={ALL}>
        <GroupsIcon sx={{ fontSize: 18 }} />
        {allLabel}
      </MenuItem>
      {teams.map((team) => (
        <MenuItem key={team.id} value={String(team.id)}>
          <TeamLogo team={team} />
          {team.name}
        </MenuItem>
      ))}
    </Select>
  );
};

export default TeamFilter;
